import type * as React from "react";
import {
  Popover as AriaPopover,
  PopoverContext,
  type PopoverProps,
  useContextProps,
} from "react-aria-components";
import { size, sizePx } from "../sizes.ts";
import { useElementWidth } from "../utils/elementSize.ts";
import { popover } from "./Popover.css.ts";
import type { OmittedReactAriaPropsWithChildren } from "./types.ts";

export type PopoverWidth = "triggerRef" | "unbounded" | keyof typeof size;

export interface Props extends Omit<
  PopoverProps,
  OmittedReactAriaPropsWithChildren
> {
  /** Width of the popover. Use "triggerRef" to match trigger element width (default), "unbounded" to let content determine width, or a size token. */
  width?: PopoverWidth;
  /** Ref to the underlying element. */
  ref?: React.Ref<HTMLElement>;
}

export const POPOVER_OFFSET = sizePx[2];
const POPOVER_MAX_HEIGHT =
  /* ~10 items */
  320 +
  /* border top and bottom */
  2;

interface PopoverWithTriggerWidthProps {
  children: Props["children"];
  props: PopoverProps & { triggerRef: NonNullable<PopoverProps["triggerRef"]> };
  forwardedRef: React.Ref<HTMLElement>;
}

function hasTriggerRef(
  props: PopoverProps,
): props is PopoverWithTriggerWidthProps["props"] {
  return props.triggerRef != null;
}

const PopoverWithTriggerWidth: React.FC<PopoverWithTriggerWidthProps> = ({
  children,
  props,
  forwardedRef,
}) => {
  const triggerWidth = useElementWidth(props.triggerRef);

  return (
    <AriaPopover
      ref={forwardedRef}
      placement="bottom"
      offset={POPOVER_OFFSET}
      maxHeight={POPOVER_MAX_HEIGHT}
      {...props}
      style={{ width: triggerWidth }}
      className={popover}
    >
      {children}
    </AriaPopover>
  );
};

/**
 * A floating container that appears relative to a trigger element.
 * Used as the foundation for dropdowns, tooltips, and other overlay components.
 */
export const Popover: React.FC<Props> = ({
  children,
  width = "triggerRef",
  ref: incomingRef,
  ...incomingProps
}) => {
  const [props, ref] = useContextProps(
    incomingProps,
    incomingRef,
    PopoverContext,
  );

  if (width === "triggerRef" && hasTriggerRef(props)) {
    return (
      <PopoverWithTriggerWidth props={props} forwardedRef={ref}>
        {children}
      </PopoverWithTriggerWidth>
    );
  }

  return (
    <AriaPopover
      ref={ref}
      placement="bottom"
      offset={POPOVER_OFFSET}
      maxHeight={POPOVER_MAX_HEIGHT}
      {...props}
      style={
        width === "triggerRef" || width === "unbounded"
          ? undefined
          : { width: size[width] }
      }
      className={popover}
    >
      {children}
    </AriaPopover>
  );
};
